import {
  arrangementSettingsPatch,
  arrangementSettingsValues,
  songByline,
  songDetailsPatch,
  songDetailsValues,
  type ArrangementSettingsSource,
  type ArrangementSettingsValues,
  type SongDetailsValues,
} from '@church/domain'
import { useState } from 'react'
import type { PreviewMeta } from './usePreviewPlayer.ts'
import { useSongRecording } from './useSongCatalog.ts'
import { useOwnRecordingPlayer } from './useSongMedia.ts'
import { useSongEditor, type Song } from './useServices.ts'

/**
 * The song page (docs/15-MUSIC-AND-AI.md): its header — the art, the title,
 * who it is by, and the one play button, which plays the church's own
 * recording whole (with a scrubber) or else Apple's 30-second preview — and
 * its edit panels, whose Save sends only what changed.
 */

/** The header: the byline, the muted line, and what its play button plays. */
export function useSongHero(song: Song) {
  const recording = useSongRecording(song)
  const own = useOwnRecordingPlayer(song)
  const facts = recording.facts
  const preview: { url: string; meta: PreviewMeta; appleMusicUrl: string | null } | null =
    !own.available && recording.shown && facts?.previewUrl
      ? {
          url: facts.previewUrl,
          meta: { title: facts.title, artist: facts.artist, artworkUrl: facts.artwork(256) },
          appleMusicUrl: facts.url,
        }
      : null
  return {
    ...songByline(song, song.arrangements.length),
    /** The church's own recording, played whole: its button and scrubber (`available` with one). */
    own,
    /** With none of its own: Apple's preview, always beside its "Apple Music" link. */
    preview,
    /** Apple Music is set up here (its card shows). */
    appleMusic: recording.shown,
  }
}

/**
 * The song's edit panel: its title, then Details, CCLI and Themes as tabs,
 * Save top right. Fresh from the song each time it opens; Save sends the
 * fields that changed and closes it.
 */
export function useSongDetailsForm(song: Song, open: boolean) {
  const editor = useSongEditor(song.id)
  const [values, setValues] = useState<SongDetailsValues>(() => songDetailsValues(song))
  const [tab, setTab] = useState<SongDetailsTab>('details')
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setValues(songDetailsValues(song))
      setTab('details')
      editor.update.reset()
    }
  }
  const { patch, problems } = songDetailsPatch(song, values)
  const dirty = Object.keys(patch).length > 0
  const invalid = Object.keys(problems).length > 0
  return {
    values,
    set: <K extends keyof SongDetailsValues>(field: K, value: SongDetailsValues[K]) =>
      setValues((was) => ({ ...was, [field]: value })),
    /** What keeps a field from saving, by field. */
    problems,
    tab,
    setTab,
    dirty,
    canSave: dirty && !invalid && !editor.update.isPending,
    pending: editor.update.isPending,
    error: editor.update.error,
    save(done: () => void) {
      if (invalid) {
        // Show the first field that can't be saved (the title is above the tabs).
        const first = Object.keys(problems).find((field) => field !== 'title')
        if (first) setTab(tabOf(first as keyof SongDetailsValues))
        return
      }
      if (!dirty) return done()
      editor.update.mutate(patch, { onSuccess: done })
    },
    /** Out of the library and the pickers (its history stays), or back: at once. */
    toggleHidden: () => editor.update.mutate({ hidden: !song.hidden }),
  }
}

export type SongDetailsTab = 'details' | 'ccli' | 'themes'

/** The tab a field is on. */
const tabOf = (field: keyof SongDetailsValues): SongDetailsTab =>
  field === 'ccliNumber' || field === 'copyright' ? 'ccli' : 'details'
export type SongDetailsForm = ReturnType<typeof useSongDetailsForm>

/** An arrangement's settings panel: name, original key, tempo, time and length; Save top right. */
export function useArrangementSettingsForm(
  songId: string,
  arrangement: ArrangementSettingsSource & { id: string },
  open: boolean,
) {
  const editor = useSongEditor(songId)
  const [values, setValues] = useState<ArrangementSettingsValues>(() =>
    arrangementSettingsValues(arrangement),
  )
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setValues(arrangementSettingsValues(arrangement))
      editor.updateArrangement.reset()
    }
  }
  const { patch, problems } = arrangementSettingsPatch(arrangement, values)
  const dirty = Object.keys(patch).length > 0
  const invalid = Object.keys(problems).length > 0
  return {
    values,
    set: <K extends keyof ArrangementSettingsValues>(
      field: K,
      value: ArrangementSettingsValues[K],
    ) => setValues((was) => ({ ...was, [field]: value })),
    problems,
    dirty,
    canSave: dirty && !invalid && !editor.updateArrangement.isPending,
    pending: editor.updateArrangement.isPending,
    error: editor.updateArrangement.error,
    save(done: () => void) {
      if (invalid) return
      if (!dirty) return done()
      editor.updateArrangement.mutate({ id: arrangement.id, ...patch }, { onSuccess: done })
    },
  }
}
